import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { clampNumber, formatBRLCurrencyFromCents } from "@/lib/currency-cents";
import { NumericKeypadContext } from "./NumericKeypadProvider";

type Props = {
  className?: string;
};

type DecimalDigits = 0 | 1 | 2;

function clampCents(cents: number, maxCents?: number) {
  const max = typeof maxCents === "number" ? maxCents : Number.MAX_SAFE_INTEGER;
  return clampNumber(cents, 0, max);
}

function applyDigitIntegerMode(cents: number, digit: number, maxCents?: number) {
  const reais = Math.floor((Number.isFinite(cents) ? cents : 0) / 100);
  const nextReais = reais * 10 + digit;
  return clampCents(nextReais * 100, maxCents);
}

function applyDigitDecimalMode(cents: number, digit: number, digits: DecimalDigits, maxCents?: number) {
  const safe = Number.isFinite(cents) ? cents : 0;
  const reais = Math.floor(safe / 100);
  const currentCentPart = safe % 100;

  if (digits === 0) {
    return clampCents(reais * 100 + digit * 10, maxCents);
  }
  if (digits === 1) {
    const tens = Math.floor(currentCentPart / 10);
    return clampCents(reais * 100 + tens * 10 + digit, maxCents);
  }
  return clampCents(safe, maxCents);
}

function backspaceIntegerMode(cents: number, maxCents?: number) {
  const reais = Math.floor((Number.isFinite(cents) ? cents : 0) / 100);
  return clampCents(Math.floor(reais / 10) * 100, maxCents);
}

function backspaceDecimalMode(cents: number, digits: DecimalDigits, maxCents?: number) {
  const safe = Number.isFinite(cents) ? cents : 0;
  const reais = Math.floor(safe / 100);
  const centPart = safe % 100;

  if (digits === 2) {
    return clampCents(reais * 100 + Math.floor(centPart / 10) * 10, maxCents);
  }
  if (digits === 1) {
    return clampCents(reais * 100, maxCents);
  }
  return clampCents(safe, maxCents);
}

export function NumericKeypadDock({ className }: Props) {
  const ctx = useContext(NumericKeypadContext);
  const field = ctx?.getActiveField() ?? null;
  const activeFieldId = field?.id ?? null;

  const [displayCents, setDisplayCents] = useState(0);
  const localCentsRef = useRef(0);

  const decimalModeByFieldRef = useRef(new Map<string, boolean>());
  const decimalDigitsByFieldRef = useRef(new Map<string, DecimalDigits>());

  // Sync with external value when active field changes
  useEffect(() => {
    if (!field) return;
    const current = field.getCents();
    localCentsRef.current = current;
    setDisplayCents(current);
  }, [activeFieldId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when external value changes (e.g. quickAction "Restante")
  const externalCents = field?.getCents() ?? 0;
  useEffect(() => {
    if (externalCents !== localCentsRef.current) {
      localCentsRef.current = externalCents;
      setDisplayCents(externalCents);
    }
  }, [externalCents]);

  // Reset decimal mode on field change
  useEffect(() => {
    if (!activeFieldId) return;
    decimalModeByFieldRef.current.set(activeFieldId, false);
    decimalDigitsByFieldRef.current.set(activeFieldId, 0);
  }, [activeFieldId]);

  const updateCents = (newVal: number) => {
    localCentsRef.current = newVal;
    setDisplayCents(newVal);
    field?.setCents(newVal);
  };

  const keys = useMemo(() => [1, 2, 3, 4, 5, 6, 7, 8, 9], []);

  if (!ctx) return null;

  const disabled = !field;

  return (
    <aside
      className={cn(
        "rounded-xl border bg-card p-3 select-none",
        "touch-manipulation",
        className
      )}
      aria-label="Teclado numérico"
    >
      <div className="space-y-2">
        <div className="rounded-lg border bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">
            {field ? field.label : "Toque em um campo para digitar"}
          </p>
          <p className="text-2xl font-semibold tabular-nums">
            {formatBRLCurrencyFromCents(displayCents)}
          </p>
        </div>

        {field?.quickActions?.length ? (
          <div className="grid grid-cols-2 gap-2">
            {field.quickActions.map((a) => (
              <Button
                key={a.id}
                type="button"
                variant="secondary"
                className="h-12"
                onClick={a.onPress}
              >
                {a.label}
              </Button>
            ))}
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-2">
          {keys.map((n) => (
            <Button
              key={n}
              type="button"
              variant="outline"
              className="h-14 text-xl"
              disabled={disabled}
              onClick={() => {
                if (!field || !activeFieldId) return;
                const currentCents = localCentsRef.current;
                const isDecimal = decimalModeByFieldRef.current.get(activeFieldId) ?? false;
                const digits = (decimalDigitsByFieldRef.current.get(activeFieldId) ?? 0) as DecimalDigits;

                if (!isDecimal) {
                  updateCents(applyDigitIntegerMode(currentCents, n, field.maxCents));
                  return;
                }
                updateCents(applyDigitDecimalMode(currentCents, n, digits, field.maxCents));
                decimalDigitsByFieldRef.current.set(activeFieldId, Math.min(2, digits + 1) as DecimalDigits);
              }}
            >
              {n}
            </Button>
          ))}

          <Button
            type="button"
            variant="outline"
            className="h-14 text-xl"
            disabled={disabled}
            onClick={() => {
              if (!field || !activeFieldId) return;
              decimalModeByFieldRef.current.set(activeFieldId, true);
              decimalDigitsByFieldRef.current.set(activeFieldId, 0);
            }}
          >
            ,
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-14 text-xl"
            disabled={disabled}
            onClick={() => {
              if (!field || !activeFieldId) return;
              const currentCents = localCentsRef.current;
              const isDecimal = decimalModeByFieldRef.current.get(activeFieldId) ?? false;
              const digits = (decimalDigitsByFieldRef.current.get(activeFieldId) ?? 0) as DecimalDigits;

              if (!isDecimal) {
                updateCents(applyDigitIntegerMode(currentCents, 0, field.maxCents));
                return;
              }
              updateCents(applyDigitDecimalMode(currentCents, 0, digits, field.maxCents));
              decimalDigitsByFieldRef.current.set(activeFieldId, Math.min(2, digits + 1) as DecimalDigits);
            }}
          >
            0
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-14 text-xl"
            disabled={disabled}
            onClick={() => {
              if (!field || !activeFieldId) return;
              const currentCents = localCentsRef.current;
              const isDecimal = decimalModeByFieldRef.current.get(activeFieldId) ?? false;
              const digits = (decimalDigitsByFieldRef.current.get(activeFieldId) ?? 0) as DecimalDigits;

              if (isDecimal) {
                if (digits > 0) {
                  updateCents(backspaceDecimalMode(currentCents, digits, field.maxCents));
                  decimalDigitsByFieldRef.current.set(activeFieldId, Math.max(0, digits - 1) as DecimalDigits);
                  return;
                }
                decimalModeByFieldRef.current.set(activeFieldId, false);
                updateCents(backspaceIntegerMode(currentCents, field.maxCents));
                return;
              }
              updateCents(backspaceIntegerMode(currentCents, field.maxCents));
            }}
          >
            ⌫
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="secondary"
            className="h-12"
            disabled={disabled}
            onClick={() => {
              if (!field) return;
              localCentsRef.current = 0;
              setDisplayCents(0);
              if (field.onClear) {
                field.onClear();
              } else {
                field.setCents(0);
              }
              if (activeFieldId) {
                decimalModeByFieldRef.current.set(activeFieldId, false);
                decimalDigitsByFieldRef.current.set(activeFieldId, 0);
              }
            }}
          >
            Limpar
          </Button>
          <Button
            type="button"
            className="h-12"
            disabled={disabled}
            onClick={() => ctx.setActiveFieldId(null)}
          >
            OK
          </Button>
        </div>
      </div>
    </aside>
  );
}
